import { generateCssString } from '@modules/global-style';
import type { Preset } from '@/types/global-style';
import { app } from '@/store';
import { base } from '@/app/editor/sidebar/constant';
import { ELEMENTS_MAP } from '@/constants/elements-map';
import { MODULES_MAP } from '@/constants/modules-map';
import { ROWS_MAP } from '@/constants/rows-map';
import HELPER from '@/app/utils/HELPER';
import { renderTabSettings } from '@/app/editor/sidebar/global-style/renderTabSetting';
import { DataSettingGlobalStyle } from '@/app/editor/iframe/atom/service/data/DataSettingGlobalStyle';

export const MER_MAP = {
  ...ELEMENTS_MAP,
  ...MODULES_MAP,
  ...ROWS_MAP,
};

const replaceCSSFromGlobalStyle = (params: { styleMatch: any; selector: string; cssAttrs: any[] }) => {
  let newCSS = params.styleMatch[0];
  if (newCSS && typeof newCSS === 'string') {
    const matchRegex = new RegExp(`${params.selector}(\\S*)?{(.|\n)*?\\}`, 'g');
    const matcheds: any = newCSS.match(matchRegex);
    const legMatched = matcheds ? matcheds.length : [];
    if (legMatched > 0) {
      for (let idx = 0; idx < legMatched; idx++) {
        const matched = matcheds[idx];
        if (typeof matched === 'string') {
          if (params.cssAttrs) {
            const cssLineRegex = new RegExp('(.?\\s)(' + params.cssAttrs + ').*?;', 'g');
            const styleReplace = matched.replace(cssLineRegex, '');
            newCSS = newCSS.replace(matched, styleReplace);
          }
        }
      }
    }
  }
  return newCSS;
};

export const getNewStyle = (styleValue: any, params: { merId: any; selector: string; cssAttrs: any }) => {
  const _xsRegexWindow = /@media(.*)width(.*)767.2px[^{]+\{([\s\S]+?})\s*}/g;
  const _xsRegex = /@media(.*)max-width(.*)767px[^{]+\{([\s\S]+?})\s*}/g;
  const _smRegex = /@media(.*)max-width(.*)991px(.*)and(.*)min-width(.*)768px[^{]+\{([\s\S]+?})\s*}/g;
  const _mdRegex = /@media(.*)max-width(.*)1199px(.*)and(.*)min-width(.*)992px[^{]+\{([\s\S]+?})\s*}/g;
  const _lgRegex = /@media(.*)min-width(.*)1200px[^{]+\{([\s\S]+?})\s*}/g;

  const xsBlock = styleValue.match(_xsRegex);
  const xsRegexWindowBlock = styleValue.match(_xsRegexWindow);
  const smBlock = styleValue.match(_smRegex);
  const mdBlock = styleValue.match(_mdRegex);
  const lgBlock = styleValue.match(_lgRegex);
  const targetSelector = params.selector
    .replace('#{eID}', params.merId)
    .replace('#{mID}', params.merId)
    .replace('#{rID}', params.merId)
    .replace('#{titleTag}', 'h');
  let newStyleValue = styleValue;
  if (xsBlock) {
    const xsCSS = replaceCSSFromGlobalStyle({
      styleMatch: xsBlock,
      selector: targetSelector,
      cssAttrs: params.cssAttrs,
    });
    newStyleValue = newStyleValue.replace(xsBlock[0], xsCSS);
  }
  if (xsRegexWindowBlock) {
    const xsWindowBlockCSS = replaceCSSFromGlobalStyle({
      styleMatch: xsRegexWindowBlock,
      selector: targetSelector,
      cssAttrs: params.cssAttrs,
    });
    newStyleValue = newStyleValue.replace(xsRegexWindowBlock[0], xsWindowBlockCSS);
  }
  if (smBlock) {
    const smCSS = replaceCSSFromGlobalStyle({
      styleMatch: smBlock,
      selector: targetSelector,
      cssAttrs: params.cssAttrs,
    });
    newStyleValue = newStyleValue.replace(smBlock[0], smCSS);
  }
  if (mdBlock) {
    const mdCSS = replaceCSSFromGlobalStyle({
      styleMatch: mdBlock,
      selector: targetSelector,
      cssAttrs: params.cssAttrs,
    });
    newStyleValue = newStyleValue.replace(mdBlock[0], mdCSS);
  }
  if (lgBlock) {
    const lgCSS = replaceCSSFromGlobalStyle({
      styleMatch: lgBlock,
      selector: targetSelector,
      cssAttrs: params.cssAttrs,
    });
    newStyleValue = newStyleValue.replace(lgBlock[0], lgCSS);
  }
  newStyleValue = HELPER.formatCSS(newStyleValue);
  // console.log(newStyleValue);
  return newStyleValue;
};

/*
 * modifyStyle
 * key: style-(module|element|row)-xxx
 * value: style
 * dataKey: heading, text-block,...
 * ...
 */
export const modifyStyle = (
  newStyleValue: any,
  dataKey: any,
  merId: any,
  fieldValues: any = [],
  $gfFrameContents: any,
) => {
  let fieldLength = fieldValues.length;
  let gsIds: any;
  if (!(fieldValues && fieldLength > 0)) {
    const merSettingGroups = Object(MER_MAP)[dataKey]?.settingGroups;
    fieldValues = (merSettingGroups && Object.values(merSettingGroups)) || [];
    gsIds = merSettingGroups && Object.keys(merSettingGroups);
  }
  if (!$gfFrameContents) {
    $gfFrameContents = jQuery('#gfFrame').contents();
  }
  fieldLength = fieldValues.length;
  const attrId = renderTabSettings.getAttrIdMER(merId) || '';
  const dataSettingGlobalStyle = new DataSettingGlobalStyle(attrId);
  for (let index = 0; index < fieldLength; index++) {
    const gsId = gsIds && gsIds.length > 0 ? gsIds[index] : '';
    const fieldValue: any = fieldValues[index];
    const itemSettingGS = renderTabSettings.getSettingGlobalStyle(dataKey, gsId);
    for (const property in fieldValue) {
      const fieldItem = fieldValue[property];
      if (fieldItem) {
        let selector: any = (fieldItem && fieldItem.selector) || '';
        if (itemSettingGS && itemSettingGS.selectorSupportModifyStyle) {
          const hover = ':hover';
          if (selector.includes(hover)) {
            selector = itemSettingGS.selectorSupportModifyStyle + hover || selector;
          } else {
            selector = itemSettingGS.selectorSupportModifyStyle || selector;
          }
        }
        const cssAttrs: any = fieldItem.cssAttr;
        if (merId && gsId) {
          const classCurrentEl = dataSettingGlobalStyle.getCurrentClassGsByKey(gsId);
          const isClassCuttom = renderTabSettings.hasClassGSCustom({ valClass: classCurrentEl });
          if (!classCurrentEl || isClassCuttom) {
            continue;
          }
        }
        newStyleValue = getNewStyle(newStyleValue, { merId, selector, cssAttrs });
      }
    }
  }
  return newStyleValue;
};
export const modifyStyleStore = (
  params: { key: string; value: string; $gfFrameContents: any },
  fieldValues: any = [],
) => {
  const keyParts: any = params.key;
  const $gfFrameContents: any = params.$gfFrameContents;
  const merId = HELPER.detachKeyDataSetting(keyParts, 'id');
  if (merId) {
    const $mer = $gfFrameContents.find(merId);
    if (!$mer || !$mer.length) {
      return params.value;
    }
    const merKey = HELPER.getDataKeyMER($mer);
    return modifyStyle(params.value, merKey, merId, fieldValues, $gfFrameContents);
  }
  return params.value;
};

export const handleModifyItemStyle = (params: any) => {
  const keyStyle = params.keyStyle;
  const merKey = params.merKey;
  const merId = params.merId;
  const fieldValues = params.fieldValues ? params.fieldValues : [];
  let $gfFrameContents: any = params.gfFrameContents;
  if (!$gfFrameContents) {
    $gfFrameContents = jQuery('#gfFrame').contents();
  }
  const bkStyleKey = keyStyle && keyStyle.replace(base._style, base._bkStyle);
  const oldStyleValue = (app as any).$store.getters['editor/GET_DATA_SETTING_BY_KEY'](keyStyle);

  const isExistBkKey = (app as any).$store.getters['editor/EXIST_KEY_DATA_SETTING'](bkStyleKey);
  if (!isExistBkKey) {
    (app as any).$store.commit('editor/SET_DATA_SETTINGS', {
      id: bkStyleKey,
      value: oldStyleValue,
    });
  }
  // modify old style
  const idStyle =
    typeof keyStyle === 'string' ? keyStyle.replace('-row', '').replace('-element', '').replace('-module', '') : '';
  let newStyleValue = '';

  if (merKey && merId) {
    newStyleValue = modifyStyle(oldStyleValue, merKey, merId, fieldValues, $gfFrameContents);
  } else {
    newStyleValue = modifyStyleStore({ key: keyStyle, value: oldStyleValue, $gfFrameContents }, fieldValues);
  }

  // replace old style block

  $gfFrameContents.find(`#${idStyle} .style`).html(newStyleValue);
  (app as any).$store.commit('editor/SET_DATA_SETTINGS', {
    id: keyStyle,
    value: newStyleValue,
  });

  return newStyleValue;
};
export const handleModifyAllStyles = ($gfFrameContents: any) => {
  const allStyleKeys = (app as any).$store.getters['editor/GET_STYLE_SETTINGS']();

  for (const keyStyle of allStyleKeys) {
    handleModifyItemStyle({ keyStyle, gfFrameContents: $gfFrameContents });
  }
};

/**
 * Apply preset to current page
 */
export const applyPreset = (preset: Preset, config: any) => {
  // generate preset css
  const cssString = generateCssString(preset);
  const $gfFrameContents = jQuery('#gfFrame').contents();
  const $gryffeditor = $gfFrameContents.find('.gryffeditor');
  const $gfGsGlobalStyle = $gryffeditor.find('.gf_gs-global-style');
  if ($gfGsGlobalStyle.length) {
    $gfGsGlobalStyle.html(cssString);
  } else {
    $gryffeditor.append(`<style class="gf_gs-global-style" type="text/css">${cssString}</style>`);
  }
  // re-render current setting style (new block) => clone old style tag & remove line css that exist in global style
  // if preview, dont trigger input setting change => don't care about input
  // get all MER style from store
  // clone style to bkStyle => save to store
  if (config && config.isOnApplyPreset) {
    // Run khi có action apply preset
    (app as any).$store.commit('sidebar/SET_APPLIED_GS_FOR_PAGE', true);
    handleModifyAllStyles($gfFrameContents);
    renderTabSettings.handleCheckAndUpdateClassFolowPreset($gryffeditor);
  }
  return;
};
